import React, { useCallback, useContext, useEffect, useState } from 'react';
import { useParams, useSearchParams, useLocation, useNavigate } from 'react-router-dom';
import { useTeams } from '../contexts/TeamsContext';
import { AuthContext } from '../contexts/AuthContext';
import { getApiConfiguration } from '../api/utils';
import { VipApi, VIPProfile, Risk, RiskStatusEnum, RiskSeverityEnum, RisksMetadata200Response } from 'api-client';
import { RisksStatistics200Response } from 'api-client/models';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/Badge';
import { AlertCircle, Mail, Phone, Building2, Shield, Filter, Calendar, X, ChevronDown } from 'lucide-react';
import { VIPRisksTable } from '../components/vip/VIPRisksTable';
import { AssetNotes } from '../components/assets/detail/AssetNotes';
import { Button } from '../components/ui/button';
import { format, addDays, parse } from 'date-fns';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { SearchInput } from '../components/findings/filters/SearchInput';
import { FilterSelect } from '@/components/findings/filters/FilterSelect';
import { toast } from 'sonner';
import RiskStatisticsPanel from '../components/vip/RiskStatisticsPanel';
// import { CalendarCell, CalendarGrid, DateInput, DateRangePicker, DateSegment, Dialog, Group, Heading, Label, Popover, RangeCalendar } from 'react-aria-components';
interface RiskStats {
  critical: number;
  high: number;
  medium: number;
  low: number;
  total: number;
  active: number;
}

const VIPProfileDetail = () => {
  const { id } = useParams<{ id: string }>();
  const { currentTeam } = useTeams();
  const { token } = useContext(AuthContext);
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  
  const [profile, setProfile] = useState<VIPProfile | null>(null);
  const [risks, setRisks] = useState<Risk[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  const [currentPage, setCurrentPage] = useState(() => {
    const page = searchParams.get('page');
    return page ? parseInt(page, 10) : 1;
  });
  const [paginationInfo, setPaginationInfo] = useState<{
    count: number;
    next: string | null;
    previous: string | null;
  }>({ count: 0, next: null, previous: null });
  const ITEMS_PER_PAGE = 10;

  const [riskStats, setRiskStats] = useState<RiskStats>({
    critical: 0,
    high: 0,
    medium: 0,
    low: 0,
    total: 0,
    active: 0,
  });
  
  // State for API-based risk statistics
  const [statistics, setStatistics] = useState<RisksStatistics200Response>({});
  const [isLoadingStats, setIsLoadingStats] = useState(false);

  const [riskMetadata, setRiskMetadata] = useState<RisksMetadata200Response | null>(null);
  
  const [searchQuery, setSearchQuery] = useState(searchParams.get('search') || '');
  const [selectedStatus, setSelectedStatus] = useState(searchParams.get('status') || 'all');
  const [selectedSeverity, setSelectedSeverity] = useState(searchParams.get('severity') || 'all');
  const [selectedSource, setSelectedSource] = useState(searchParams.get('source') || 'all');
  const [selectedType, setSelectedType] = useState(searchParams.get('type') || 'all');

  const [dateRange, setDateRange] = useState<{
    startDate: Date | undefined;
    endDate: Date | undefined;
    key: string;
  }>(() => {
    const from = searchParams.get('from');
    const to = searchParams.get('to');
    return {
      startDate: from ? new Date(from) : undefined,
      endDate: to ? new Date(to) : undefined,
      key: 'selection',
    };
  });

  const [showDateRangePicker, setShowDateRangePicker] = useState(false);

  const statusOptions = ['all', 'ACTIVE', 'MITIGATED', 'RESOLVED'];
  const severityOptions = ['all', 'CRITICAL', 'HIGH', 'MEDIUM', 'LOW'];

  const fetchProfile = useCallback(async () => {
    if (!currentTeam?.slug || !token || !id) return;

    try {
      const api = new VipApi(getApiConfiguration(token));
      const profileData = await api.vipProfilesRetrieve({
        teamSlug: currentTeam.slug,
        id,
      });
      setProfile(profileData);
    } catch (err) {
      console.error('Error fetching VIP profile:', err);
      toast.error('Failed to fetch VIP profile');
      setProfile(null);
      // setError('Failed to fetch VIP profile');
    }
  }, [currentTeam?.slug, token, id]);

  const fetchRiskMetadata = useCallback(async () => {
    if (!currentTeam?.slug || !token || !id) return;

    try {
      const api = new VipApi(getApiConfiguration(token));
      const metadata = await api.risksMetadata({
        teamSlug: currentTeam.slug,
      });
      setRiskMetadata(metadata);
    } catch (err) {
      console.error('Error fetching risk metadata:', err);
      toast.error('Failed to fetch risk metadata');
      // setError('Failed to fetch risk metadata');
      setRiskMetadata(null);
    }
  }, [currentTeam?.slug, token, id]);
  
  // Fetch risk statistics from API
  const fetchStatistics = useCallback(async () => {
    if (!currentTeam?.slug || !token || !id) return;

    setIsLoadingStats(true);
    try {
      const api = new VipApi(getApiConfiguration(token));
      const response = await api.risksStatistics({
        teamSlug: currentTeam.slug,
        profile: parseInt(id, 10)
      });
      setStatistics(response);
    } catch (err) {
      console.error('Error fetching risk statistics:', err);
      toast.error('Failed to fetch risk statistics');
    } finally {
      setIsLoadingStats(false);
    }
  }, [currentTeam?.slug, token, id]);

  const buildUrlWithParams = (params: Record<string, any>) => {
    const newParams = new URLSearchParams(searchParams.toString());

    Object.entries(params).forEach(([key, value]) => {
      if (value === undefined || value === null || value === '' || value === 'all') {
        newParams.delete(key);
      } else {
        newParams.set(key, String(value));
      }
    });

    if (params.page === 1) {
      newParams.delete('page');
    }

    return `${location.pathname}?${newParams.toString()}`;
  };

  const handleSearchChange = (query: string) => {
    setSearchQuery(query);
    setCurrentPage(1);
    navigate(buildUrlWithParams({ search: query, page: 1 }), {
      state: { timestamp: new Date().getTime() },
    });
  };

  const handleStatusChange = (status: string) => {
    setSelectedStatus(status);
    setCurrentPage(1);
    navigate(buildUrlWithParams({ status, page: 1 }), {
      state: { timestamp: new Date().getTime() },
    });
  };

  const handleSeverityChange = (severity: string) => {
    setSelectedSeverity(severity);
    setCurrentPage(1);
    navigate(buildUrlWithParams({ severity, page: 1 }), {
      state: { timestamp: new Date().getTime() },
    });
  };

  const handleSourceChange = (source: string) => {
    setSelectedSource(source);
    setCurrentPage(1);
    navigate(buildUrlWithParams({ source, page: 1 }), {
      state: { timestamp: new Date().getTime() },
    });
  };

  const handleTypeChange = (type: string) => {
    setSelectedType(type);
    setCurrentPage(1);
    navigate(buildUrlWithParams({ type, page: 1 }), {
      state: { timestamp: new Date().getTime() },
    });
  };

  const handleDateRangeChange = (ranges: any) => {
    const { selection } = ranges;
    setDateRange(selection);

    if (!showDateRangePicker) {
      buildUrlWithParams({
        from: selection.startDate ? format(selection.startDate, 'yyyy-MM-dd') : undefined,
        to: selection.endDate ? format(selection.endDate, 'yyyy-MM-dd') : undefined,
      });
    }
  };

  const handleDateRangeApply = () => {
    setShowDateRangePicker(false);
    buildUrlWithParams({
      from: dateRange.startDate ? format(dateRange.startDate, 'yyyy-MM-dd') : undefined,
      to: dateRange.endDate ? format(dateRange.endDate, 'yyyy-MM-dd') : undefined,
    });
  };

  const clearDateRange = () => {
    setDateRange({
      startDate: undefined,
      endDate: undefined,
      key: 'selection',
    });
    buildUrlWithParams({ from: undefined, to: undefined });
    setShowDateRangePicker(false);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    navigate(buildUrlWithParams({ page }), { 
      state: { timestamp: new Date().getTime() } 
    });
  };

  const handleReset = () => {
    setSearchQuery('');
    setSelectedStatus('all');
    setSelectedSeverity('all');
    setSelectedSource('all');
    setSelectedType('all');
    setDateRange({
      startDate: undefined,
      endDate: undefined,
      key: 'selection'
    });
    setCurrentPage(1);
    navigate(location.pathname, { 
      state: { timestamp: new Date().getTime() } 
    });
  };

  const fetchRisks = useCallback(async () => {
    if (!currentTeam?.slug || !token || !id) return;

    try {
      const api = new VipApi(getApiConfiguration(token));
      const response = await api.risksList({
        teamSlug: currentTeam.slug,
        page: currentPage,
        profile: Number(id),
        search: searchQuery || undefined,
        status: selectedStatus !== 'all' ? selectedStatus as RiskStatusEnum : undefined,
        severity: selectedSeverity !== 'all' ? selectedSeverity as RiskSeverityEnum : undefined,
        source: selectedSource !== 'all' ? selectedSource : undefined,
        type: selectedType !== 'all' ? selectedType : undefined,
        // createdAfter: dateRange.startDate ? format(dateRange.startDate, 'yyyy-MM-dd') : undefined,
        // createdBefore: dateRange.endDate ? format(dateRange.endDate, 'yyyy-MM-dd') : undefined,
      });
      
      setRisks(response.results);
      setPaginationInfo({
        count: response.count,
        next: response.next || null,
        previous: response.previous || null,
      });

      // Calculate risk statistics
      const stats = response.results.reduce((acc, risk) => {
        acc.total++;
        if (risk.status === RiskStatusEnum.Active) {
          acc.active++;
        }
        switch (risk.severity) {
          case RiskSeverityEnum.Critical:
            acc.critical++;
            break;
          case RiskSeverityEnum.High:
            acc.high++;
            break;
          case RiskSeverityEnum.Medium:
            acc.medium++;
            break;
          case RiskSeverityEnum.Low:
            acc.low++;
            break;
        }
        return acc;
      }, {
        critical: 0,
        high: 0,
        medium: 0,
        low: 0,
        total: 0,
        active: 0,
      } as RiskStats);

      setRiskStats(stats);
    } catch (err) {
      console.error('Error fetching VIP risks:', err);
      toast.error('Failed to fetch VIP risks');
      // setError('Failed to fetch VIP risks');
      setRisks([]);
      setRiskStats({
        critical: 0,
        high: 0,
        medium: 0,
        low: 0,
        total: 0,
        active: 0,
      });
    }
  }, [currentTeam?.slug, token, id, currentPage, searchQuery, selectedStatus, selectedSeverity, selectedSource, selectedType, dateRange]);

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      await Promise.all([fetchProfile()]);
      setIsLoading(false);
    };

    fetchData();
  }, [fetchProfile]);

  useEffect(() => {
    fetchRisks();
    fetchRiskMetadata();
    fetchStatistics();
  }, [fetchRisks, fetchRiskMetadata, fetchStatistics]);

  // This is now defined above with the other filter handlers

  if (isLoading) return <div>Loading...</div>;
  if (error) return <div className="text-red-500">{error}</div>;
  if (!profile) return <div>Profile not found</div>;

  const initials = profile?.name
    ?.split(' ')
    ?.map(n => n[0])
    ?.join('')
    ?.toUpperCase();

  return (
    <div className="space-y-6 p-6">
      {/* Profile Card */}
      <Card className="bg-[#0F1629] border-gray-800/50">
        <CardContent className="p-6">
          <div className="space-y-4">
            {/* Avatar and Name */}
            <div className="flex items-start space-x-4">
              <div className="h-20 w-20 rounded-full overflow-hidden bg-gray-800/50 flex items-center justify-center">
                {profile?.profilePicture ? (
                  <img
                    src={profile?.profilePicture}
                    alt={profile?.name}
                    className="h-full w-full object-cover"
                  />
                ) : (
                  <span className="text-2xl font-semibold text-gray-400">{initials}</span>
                )}
              </div>
              <div className="flex-1">
                <h1 className="text-2xl font-semibold text-white">{profile?.name || 'Unknown Name'}</h1>
                <div className="flex items-center space-x-1 text-lg text-gray-400">
                  <Building2 className="h-5 w-5" />
                  <span>{profile?.title || 'Unknown Title'}</span>
                </div>
              </div>
            </div>

            {/* Contact Info */}
            <div className="space-y-2">
              <div className="flex items-center space-x-2 text-gray-400">
                <Mail className="h-5 w-5" />
                <span>{profile?.email || 'Unknown Email'}</span>
              </div>
              <div className="flex items-center space-x-2 text-gray-400">
                <Phone className="h-5 w-5" />
                <span>{profile?.phoneNumber || 'Unknown Phone Number'}</span>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Risk Details */}
      <div className="mt-6">
        {/* API-based Risk Statistics */}
        <RiskStatisticsPanel 
          statistics={statistics} 
          isLoading={isLoadingStats} 
          onRefresh={fetchStatistics} 
          title="Risk Statistics"
        />
        
        {/* Legacy Risk Stats Cards - Hidden now that we're using the reusable component */}
        <div className="hidden">
          <h2 className="text-xl font-semibold text-white mb-4">Risk Information</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            <Card className="bg-[#0F1629] border-gray-800/50">
              <CardContent className="p-4">
                <h3 className="text-lg font-semibold text-white mb-2">Critical Risks</h3>
                <div className="flex items-center space-x-2">
                  <AlertCircle className="h-5 w-5 text-red-500" />
                  <span className="text-2xl font-bold text-white">{riskStats.critical}</span>
                </div>
              </CardContent>
            </Card>
            <Card className="bg-[#0F1629] border-gray-800/50">
              <CardContent className="p-4">
                <h3 className="text-lg font-semibold text-white mb-2">High Risks</h3>
                <div className="flex items-center space-x-2">
                  <AlertCircle className="h-5 w-5 text-orange-500" />
                  <span className="text-2xl font-bold text-white">{riskStats.high}</span>
                </div>
              </CardContent>
            </Card>
            <Card className="bg-[#0F1629] border-gray-800/50">
              <CardContent className="p-4">
                <h3 className="text-lg font-semibold text-white mb-2">Medium Risks</h3>
                <div className="flex items-center space-x-2">
                  <Shield className="h-5 w-5 text-yellow-500" />
                  <span className="text-2xl font-bold text-white">{riskStats.medium}</span>
                </div>
              </CardContent>
            </Card>
            <Card className="bg-[#0F1629] border-gray-800/50">
              <CardContent className="p-4">
                <h3 className="text-lg font-semibold text-white mb-2">Active Risks</h3>
                <div className="flex items-center space-x-2">
                  <Shield className="h-5 w-5 text-blue-500" />
                  <span className="text-2xl font-bold text-white">{riskStats.active}</span>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Filters Section to build*/}
      <div className="mt-6 space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-xl font-semibold text-white">Risk History</h2>
          <Button 
            variant="outline" 
            size="sm" 
            onClick={handleReset}
            className="text-gray-400 hover:text-white"
          >
            <X className="h-4 w-4 mr-2" />
            Reset Filters
          </Button>
        </div>
        
        {/* Search and Filters */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-4">
          {/* Search Bar */}
          <div className="lg:col-span-2">
            <SearchInput
              value={searchQuery}
              onChange={handleSearchChange}
              placeholder="Search risks..."
            />
          </div>
          
          {/* Status Filter */}
          <FilterSelect
            options={statusOptions.map(status => ({ value: status, label: status === "all" ? "All Statuses" : status }))}
            value={selectedStatus}
            onValueChange={handleStatusChange}
            placeholder="Status"
            icon={<Filter className="h-4 w-4 mr-2 text-gray-500" />}
            searchable
            scrollable
          />
          
          {/* Severity Filter */}
          <FilterSelect
            options={severityOptions.map(severity => ({ value: severity, label: severity === "all" ? "All Severities" : severity }))}
            value={selectedSeverity}
            onValueChange={handleSeverityChange}
            placeholder="Severity"
            icon={<AlertCircle className="h-4 w-4 mr-2 text-gray-500" />}
            searchable
            scrollable
            // width="w-max min-w-[156px]"
          />
          
          {/* Source Filter */}
          <FilterSelect
            options={[...riskMetadata?.sources || [], "all"].map(source => ({ value: source, label: source === "all" ? "All Sources" : source })) || []}
            value={selectedSource}
            onValueChange={handleSourceChange}
            placeholder="Source"
            icon={<Shield className="h-4 w-4 mr-2 text-gray-500" />}
            searchable
            scrollable
          />
          
          {/* Type Filter */}
          <FilterSelect
            options={[...riskMetadata?.types || [], "all"].map(type => ({ value: type, label: type === "all" ? "All Types" : type })) || []}
            value={selectedType}
            onValueChange={handleTypeChange}
            placeholder="Type"
            icon={<Filter className="h-4 w-4 mr-2 text-gray-500" />}
            searchable
            scrollable
          />
          
        </div>
        
        {/* Date Range Filter */}
        <div className="relative">
          {/* <Button
            variant="outline"
            className="justify-start text-left font-normal bg-[#0F1629] border-gray-700 text-white w-full sm:w-auto"
            onClick={() => setShowDateRangePicker(!showDateRangePicker)}
          >
            <Calendar className="mr-2 h-4 w-4 text-gray-500" />
            {dateRange.startDate || dateRange.endDate ? (
              <span>
                {dateRange.startDate ? format(dateRange.startDate, 'MMM dd, yyyy') : 'Any'} - {dateRange.endDate ? format(dateRange.endDate, 'MMM dd, yyyy') : 'Any'}
              </span>
            ) : (
              <span className="text-gray-500">Date Range</span>
            )}
          </Button>
          
          {(dateRange.startDate || dateRange.endDate) && (
            <Button
              variant="ghost"
              size="sm"
              className="ml-2 text-gray-400 hover:text-white"
              onClick={clearDateRange}
            >
              Clear
            </Button>
          )} */}
          
          {/* {showDateRangePicker && (
            <div className="absolute z-50 mt-2 bg-[#1A1F2E] border border-gray-700 rounded-md shadow-lg">
              <DateRangePicker>
              <Label>Date Range</Label>
              <Group>
                <DateInput slot="start">
                  {(segment) => <DateSegment segment={segment} />}
                </DateInput>
                <span aria-hidden="true">–</span>
                <DateInput slot="end">
                  {(segment) => <DateSegment segment={segment} />}
                </DateInput>
                <Button>▼</Button>
              </Group>
              <Popover>
                <Dialog>
                  <RangeCalendar>
                    <header>
                      <Button slot="previous">◀</Button>
                      <Heading />
                      <Button slot="next">▶</Button>
                    </header>
                    <CalendarGrid>
                      {(date) => <CalendarCell date={date} />}
                    </CalendarGrid>
                  </RangeCalendar>
                </Dialog>
              </Popover>
            </DateRangePicker>
              <div className="p-3 border-t border-gray-700 flex justify-between">
                <Button
                  variant="ghost"
                  className="text-gray-400 hover:text-white"
                  onClick={clearDateRange}
                >
                  Clear
                </Button>
                <Button
                  className="bg-blue-600 hover:bg-blue-700 text-white"
                  onClick={handleDateRangeApply}
                >
                  Apply
                </Button>
              </div>
            </div>
          )} */}
        </div>
        
        {/* Risks Table */}
        <VIPRisksTable
          risks={risks}
          currentPage={currentPage}
          onPageChange={handlePageChange}
          totalRisks={paginationInfo.count}
          itemsPerPage={ITEMS_PER_PAGE}
          paginationInfo={paginationInfo}
        />
      </div>
      </div>
    </div>
  );
};

export default VIPProfileDetail;
